import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as PayoutHistoryIcon } from '../../../assets/icons/profile/payout-history-icon.svg';
import { ReactComponent as RevenueIcon } from '../../../assets/icons/profile/revenueIcon.svg';
import { brandIcons, brandText } from '../../../config/branding';
import {
  Last12MonthProperties,
  Last24HoursProperties,
  Last7DaysProperties,
  LastMonthProperties,
} from '../../../interface/partner-interfaces/dashboard.interface';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slices/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
import getFirstSubdomain from '../../../shared-functions/DomainsplitFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import dashboard from '../../../utils/api/partner/dashboard';
import profile from '../../../utils/api/partner/profile';

import RevenueExpenseChart from './revenueGraph';

interface ChartDataProperties {
  label: string;
  totalDebitAmount: number;
}

interface FillMissing24hrsProperties {
  date: string;
  label: number;
  totalDebitAmount: number;
}

/**
 * Function to handle missing data for last 24 hrs
 * To-do - Need to move this function inside the scope ( eslint error occurs )
 * @param {any} data - The data
 * @returns {any} The result
 */
function fillMissingLast24HrsData(data: FillMissing24hrsProperties[]) {
  const today = new Date();
  const past24Hours = [];

  // Generate the past 24 hours
  for (let index = 0; index < 24; index += 1) {
    const date = new Date(today);
    date.setHours(today.getHours() - index, 0, 0, 0); // Adjust the hour and reset minutes, seconds, and ms
    const isoDate = date.toISOString().split('T')[0];
    past24Hours.push({ date: isoDate, hour: date.getHours() });
  }

  // Map backend data into a dictionary for easy lookup
  const revenueMap = new Map(
    data.map((item) => [`${item.date}-${item.label}`, item.totalDebitAmount]),
  );

  // Build the result array
  const result = past24Hours.map(({ date, hour }) => {
    const key = `${date}-${hour}`; // Construct key for lookup
    return {
      // date, // ISO date
      label: hour.toString(), // Hour (0-23)
      totalDebitAmount: revenueMap.get(key) || 0, // Default 0
    };
  });

  return result.reverse();
}

/**
 * Function to fill missing months in chart
 * @param {any} data - The data
 * @returns {any} The result
 */
function fillMissingMonths(data: ChartDataProperties[]) {
  const today = new Date();
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  // Generate past 12 months dynamically
  const past12Months = [];
  for (let index = 0; index < 12; index += 1) {
    const date = new Date(today.getFullYear(), today.getMonth() - index, 1); // Get the first day of the month
    past12Months.push({
      label: months[date.getMonth()], // Month name
      year: date.getFullYear(), // Year
    });
  }

  // Map input data for easy lookup
  const revenueMap = new Map(data.map((item) => [item.label, item.totalDebitAmount]));

  // Generate result array with missing months filled
  const result = past12Months.map(({ label, year }) => {
    const key = label; // Use the month name as key
    return {
      label: `${label} ${year}`, // Combine month and year for output
      totalDebitAmount: revenueMap.get(key) || 0, // Default 0
    };
  });

  return result.reverse();
}

/**
 * Function to generate the map data for past 30 days
 * @param {any} data - The data
 * @param {any} i18n - The i18n
 * @returns {any} The result
 */
function fillMissingLastMonth(data: ChartDataProperties[], i18n: any) {
  const today = new Date();
  const past30Days = [];

  // Generate the past 30 days as ISO date strings
  for (let index = 0; index < 30; index += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - index);
    past30Days.push(date);
  }

  // Map backend data into a dictionary for easy lookup
  const revenueMap = new Map(data.map((item) => [item.label.split('T')[0], item.totalDebitAmount]));

  // Date formatter to get "Nov 18" format
  const dateFormatter = new Intl.DateTimeFormat(i18n.language, {
    month: 'short',
    day: 'numeric',
  });

  // Build the result array
  const result = past30Days.map((date) => {
    const isoDate = date.toISOString().split('T')[0]; // Use the ISO date for lookup
    return {
      label: dateFormatter.format(date), // Format label as "Nov 18"
      totalDebitAmount: revenueMap.get(isoDate) || 0, // Default 0
    };
  });

  return result.reverse();
}

/**
 * Function to add missing data in last 7 days
 * @param {any} data - The data
 * @param {any} i18n - The i18n
 * @returns {any} The result
 */
function fillMissingLast7Days(data: ChartDataProperties[], i18n: any) {
  const today = new Date();
  const last7Days = [];

  // Generate the last 7 days as ISO date strings
  for (let index = 0; index < 7; index += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - index);
    last7Days.push(date);
  }

  // Map backend data into a dictionary for easy lookup
  const revenueMap = new Map(data.map((item) => [item.label.split('T')[0], item.totalDebitAmount]));

  // Date formatter to get "Nov 14" format
  const dateFormatter = new Intl.DateTimeFormat(i18n.language, {
    month: 'short',
    day: 'numeric',
  });

  // Build the result array
  const result = last7Days.map((date) => {
    const isoDate = date.toISOString().split('T')[0]; // Use ISO date for lookup
    return {
      label: dateFormatter.format(date), // Format label as "Nov 14"
      totalDebitAmount: revenueMap.get(isoDate) || 0, // Default 0
    };
  });

  return result.reverse();
}

/**
 * View the revenue details
 * @param {any} param0 - The param0
 * @param {any} param0.withdrawalHistoryChange - The withdrawalHistoryChange prop
 * @returns {any} The result
 */
function RevenueDetails({ withdrawalHistoryChange }: { withdrawalHistoryChange: () => void }) {
  const { t, i18n } = useTranslation('profile');
  const dispatch = useAppDispatch();
  const [totalBalance, setTotalBalance] = useState<number>(0);

  const [powerConsumedData, setPowerConsumedData] = useState<ChartDataProperties[]>([]);
  const [totalPayout, setTotalPayout] = useState(0);
  const currentBranding = process.env.REACT_APP_BRANDING;
  const timePeriod: {
    label: string;
    value: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months';
  }[] = [
      {
        label: '12months',
        value: 'last12months',
      },
      {
        label: 'lastMonth',
        value: 'lastMonth',
      },
      {
        label: '7days',
        value: 'last7days',
      },
      {
        label: '24hours',
        value: 'last24hours',
      },
    ];

  /**
   * Function to fetch total balance and withdrawable balance
   */
  const fetchWalletBalance = async () => {
    try {
      const response = await profile.getTotalWalletBalance();
      setTotalBalance(response?.data?.revenue || 0);
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Fetch power consumed data based on time range
   * @param {any} range - The range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    try {
      const response = await dashboard.getPowerConsumedReport({ timePeriod: range });
      if (range === 'last12months' && response.status === 200) {
        const updatedMapData: ChartDataProperties[] = response.data.map(
          (eachMonth: Last12MonthProperties) => ({
            label: eachMonth.monthName,
            powerConsumed: eachMonth.totalPowerConsumed,
            totalDebitAmount: eachMonth.totalDebitAmount,
          }),
        );
        setPowerConsumedData(fillMissingMonths(updatedMapData));
      } else if (range === 'lastMonth' && response.status === 200) {
        const updatedMapData: ChartDataProperties[] = response.data.map(
          (eachMonth: LastMonthProperties) => ({
            label: eachMonth.date,
            powerConsumed: eachMonth.totalPowerConsumed,
            totalDebitAmount: eachMonth.totalDebitAmount,
          }),
        );
        setPowerConsumedData(fillMissingLastMonth(updatedMapData, i18n));
      } else if (range === 'last7days' && response.status === 200) {
        const updatedMapData: ChartDataProperties[] = response.data.map(
          (eachMonth: Last7DaysProperties) => ({
            label: eachMonth.date,
            powerConsumed: eachMonth.totalPowerConsumed,
            totalDebitAmount: eachMonth.totalDebitAmount,
          }),
        );
        setPowerConsumedData(fillMissingLast7Days(updatedMapData, i18n));
      } else if (range === 'last24hours' && response.status === 200) {
        const updatedMapData: {
          date: string;
          label: number;
          powerConsumed: number;
          totalDebitAmount: number;
        }[] = response.data.map((eachMonth: Last24HoursProperties) => ({
          label: eachMonth.hour,
          date: eachMonth.date,
          powerConsumed: eachMonth.totalPowerConsumed,
          totalDebitAmount: eachMonth.totalDebitAmount,
        }));
        setPowerConsumedData(fillMissingLast24HrsData(updatedMapData));
      }
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * fetch Total Payout Creadit
   */
  const fetchTotalPayoutCreadit = () => {
    profile
      .getRevenuePayoutAmount()
      .then((response: any) => {
        if (response?.data) {
          setTotalPayout(response.data.totalTransferred);
        }
      })
      .catch((error: any) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  useEffect(() => {
    fetchPowerConsumedReport('last12months');
    fetchWalletBalance();
    fetchTotalPayoutCreadit();
  }, []);

  const subdomain = getFirstSubdomain();
  const shouldHideAccessUtility = !!subdomain;
  return (
    <div className="revenue-details">
      <div className="revenue-header">
        <div className="revenue-title">
          <span>
            <RevenueIcon />
          </span>
          <p>{t('revenue')}</p>
        </div>
        <div className="revenue-withdrawal">
          {!shouldHideAccessUtility && (
            <MyButton
              label={t('payoutHistory')}
              disableHoverEffect
              buttonType="submit"
              onClickFunc={withdrawalHistoryChange}
              svgIcon={<PayoutHistoryIcon />}
            />
          )}
        </div>
      </div>
      <div className="revenue-balance">
        <div className="total-balance">
          <p>{t('totalRevenue')}</p>
          <div className="count">
            <brandIcons.dollarCurrency />
            <span className={currentBranding}>
              {ToFixedValue(totalBalance)} ({t(brandText.currency)})
            </span>
          </div>
        </div>
        <div className="withdrawal-balance">
          <p>{t('totalPayoutAmount')}</p>
          <div className="count">
            <brandIcons.dollarCurrency />
            <span className={currentBranding}>
              {ToFixedValue(totalPayout)} ({t(brandText.currency)})
            </span>
          </div>
        </div>
      </div>
      <div className="revenue-graph">
        <div className="graph-header">
          <p className="graph-title">{t('activity')}</p>
          <div className="graph-toggle-btn">
            <CustomToggleButton
              labels={timePeriod.map((duration) => t(duration.label))}
              onToggle={(index: number, label: string) =>
                fetchPowerConsumedReport(
                  timePeriod.find((duration) => t(duration.label) === label)?.value ||
                  'last12months',
                )
              }
              defaultActiveIndex={0}
            />
          </div>
        </div>
        <div className="revenue-graph">
          <RevenueExpenseChart powerConsumedData={powerConsumedData} />
        </div>
      </div>
    </div>
  );
}

export default RevenueDetails;
